import type { GiftCardContactMethod } from '@/@type/giftCard';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import { InputAdornment, Stack, TextField, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { MuiTelInput, type MuiTelInputCountry } from 'mui-tel-input';
import { useTranslation } from 'react-i18next';

interface MerchantGiftCardContactFieldProps {
    contactMethod: GiftCardContactMethod;
    email: string;
    phone: string;
    phoneCountry: MuiTelInputCountry;
    disabled?: boolean;
    onContactMethodChange: (value: GiftCardContactMethod) => void;
    onEmailChange: (value: string) => void;
    onPhoneChange: (value: string) => void;
    onPhoneCountryChange: (value: MuiTelInputCountry) => void;
}

const MerchantGiftCardContactField = ({
    contactMethod,
    email,
    phone,
    phoneCountry,
    disabled = false,
    onContactMethodChange,
    onEmailChange,
    onPhoneChange,
    onPhoneCountryChange
}: MerchantGiftCardContactFieldProps): JSX.Element => {
    const { t, i18n } = useTranslation();
    const countryNameLanguage = i18n.language.startsWith('zh') ? 'zh' : 'en';

    return (
        <Stack spacing={1.5}>
            <ToggleButtonGroup
                exclusive
                fullWidth
                size="small"
                value={contactMethod}
                onChange={(_, value: GiftCardContactMethod | null) => value && onContactMethodChange(value)}
                disabled={disabled}
            >
                <ToggleButton value="email">
                    {t('giftCard.useEmail')}
                </ToggleButton>
                <ToggleButton value="phone">
                    {t('giftCard.usePhoneNumber')}
                </ToggleButton>
            </ToggleButtonGroup>

            {contactMethod === 'email' ? (
                <TextField
                    fullWidth
                    type="email"
                    label={t('giftCard.emailAddress')}
                    value={email}
                    onChange={(event) => onEmailChange(event.target.value)}
                    placeholder={t('giftCard.emailPlaceholder')}
                    disabled={disabled}
                    slotProps={{
                        input: {
                            startAdornment: (
                                <InputAdornment position="start">
                                    <EmailOutlinedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                                </InputAdornment>
                            )
                        }
                    }}
                />
            ) : (
                <MuiTelInput
                    fullWidth
                    label={t('giftCard.phoneNumber')}
                    value={phone}
                    onChange={(value, info) => {
                        onPhoneChange(value);
                        if (info.countryCode) {
                            onPhoneCountryChange(info.countryCode);
                        }
                    }}
                    defaultCountry={phoneCountry}
                    preferredCountries={[phoneCountry]}
                    forceCallingCode
                    disableFormatting
                    langOfCountryName={countryNameLanguage}
                    placeholder={t('giftCard.phoneNumberPlaceholder')}
                    disabled={disabled}
                    MenuProps={{
                        PaperProps: {
                            sx: {
                                maxHeight: 360
                            }
                        }
                    }}
                />
            )}
        </Stack>
    );
};

export default MerchantGiftCardContactField;
